import {HttpClient, HttpParams} from '@angular/common/http';
import {inject, Injectable,signal , computed} from '@angular/core';
import {catchError, forkJoin, Observable, of, take} from 'rxjs';
import {IExplorerProjectsResponse} from '../../../interfaces/explorer/interfaces/IExplorerPaginatedProject';
import {environment} from '../../../../environments/environment';
import {IExplorerProject} from '../../../interfaces/explorer/interfaces/IExplorerProject';
import {
  IExplorerProjectArea,
  IExplorerProjectAreaCreate,
  IExplorerProjectAreaPatch,
} from '../../../interfaces/explorer/interfaces/IExplorerProjectArea';
import { Feature, MultiPolygon } from 'geojson';
import { Store } from '@ngrx/store';
import { Router } from '@angular/router';
import { ExplorerSjmapService } from '../explorer-sjmap/explorer-sjmap.service';
import { FireSightProjectApiService } from '../../fire-sight/fire-sight-project-api.service';
import { cleanSJMap } from '../../../store/actions/explorer/explorer-sjmap.actions';
import { cleanAnalyticsData } from '../../../store/actions/explorer/explorer-analytics-tool.actions';



@Injectable({
  providedIn: 'root',
})
export class ExplorerProjectService {

  private API_URL: string = environment.services.urlEDI;
  private ENDPOINT: string = "/explorer_project";
  private http: HttpClient = inject(HttpClient);
  private readonly _openingProjectUUID = signal<string | null>(null);
  readonly openingProjectUUID = this._openingProjectUUID.asReadonly();
  readonly isOpeningProjectDisabled = computed( () => this._openingProjectUUID() !== null
);

  constructor(
    private store: Store,
    private router: Router,
    private explorerSjmapService: ExplorerSjmapService,
    private fireSightProjectApiService: FireSightProjectApiService,
  ) {}

  getPaginatedProjects(page: number, size: number): Observable<IExplorerProjectsResponse> {
    const params = new HttpParams()
      .set('page', page)
      .set('size', size);

    return this.http.get<IExplorerProjectsResponse>(`${this.API_URL}${this.ENDPOINT}`, {params});
  }

  searchPaginatedProjects(
    page: number,
    size: number,
    name: string
  ): Observable<IExplorerProjectsResponse> {
    const params = new HttpParams()
      .set('page', page)
      .set('size', size)
      .set('name', name.trim());

    return this.http.get<IExplorerProjectsResponse>(`${this.API_URL}${this.ENDPOINT}`, {params});
  }

  createNewProject(name: string, area: IExplorerProjectAreaCreate): Observable<IExplorerProject> {
    return this.http.post<IExplorerProject>(`${this.API_URL}${this.ENDPOINT}`, {name, area});
  }

  getProjectAreas(projectUUID: string): Observable<IExplorerProjectArea[]> {
    return this.http.get<IExplorerProjectArea[]>(`${this.API_URL}${this.ENDPOINT}/${projectUUID}/areas`);
  }

  addProjectArea(projectUUID: string, area: IExplorerProjectAreaCreate): Observable<IExplorerProjectArea> {
    return this.http.post<IExplorerProjectArea>(`${this.API_URL}${this.ENDPOINT}/${projectUUID}/areas`, area);
  }

  patchProjectArea(projectUUID: string, areaUUID: string, patch: IExplorerProjectAreaPatch): Observable<IExplorerProjectArea> {
    return this.http.patch<IExplorerProjectArea>(`${this.API_URL}${this.ENDPOINT}/${projectUUID}/areas/${areaUUID}`, patch);
  }

  getProjectAreaGeoJSON(projectUUID: string, areaUUID: string): Observable<Feature<MultiPolygon>> {
    return this.http.get<Feature<MultiPolygon>>(`${this.API_URL}${this.ENDPOINT}/${projectUUID}/areas/${areaUUID}/geojson`);
  }

  exportProjectAreaGeoJSON(projectUUID: string, areaUUID: string, revision: number): Observable<IExplorerProjectArea> {
    return this.http.post<IExplorerProjectArea>(`${this.API_URL}${this.ENDPOINT}/${projectUUID}/areas/${areaUUID}/geojson`, {
      expected_geometry_revision: revision,
    });
  }

  getAllProjects(): Observable<IExplorerProject[]> {
    return this.http.get<IExplorerProject[]>(`${this.API_URL}${this.ENDPOINT}?=all`);
  }
  openProject(projectUUID: string) {
  if (this._openingProjectUUID() !== null) {
    return;
  }

  this._openingProjectUUID.set(projectUUID);

  this.store.dispatch(cleanSJMap());
  this.store.dispatch(cleanAnalyticsData({}));

  localStorage.removeItem('isEditing');
  localStorage.removeItem('masterplanUUID');
  localStorage.removeItem('accordionStates');

  forkJoin({
    sjmap: this.explorerSjmapService.getSJMapFromProject(projectUUID).pipe(
      take(1),
      catchError(() => of(null)),
    ),
    monitoringAreas: this.fireSightProjectApiService
      .getMonitoringAreas(projectUUID)
      .pipe(
        take(1),
        catchError(() => of(null)),
      ),
  })
    .subscribe(({ sjmap, monitoringAreas }) => {
      const hasNoExplorerLayers =
        sjmap !== null && sjmap.layers.length === 0;

      const hasMonitoringArea =
        monitoringAreas !== null &&
        monitoringAreas.items.length > 0;

      const route =
        hasNoExplorerLayers && hasMonitoringArea
          ? ['/explorer', 'firesight', projectUUID]
          : ['/explorer', projectUUID];

      void this.router.navigate(route)
             .then((navigated) => {
          if (!navigated) {
            this._openingProjectUUID.set(null);
          }
        })
        .catch(() => false)
  .finally(() => this._openingProjectUUID.set(null));
    });
}
}
